import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
// findCustomerByCorporateNumberとsaveCustomerDataはFunctions APIに移行したため、import不要
import { CustomerData, CompanySearchCache } from '../firebase/types';

interface CSVRow {
  companyName: string;
  transactionType: string;
  originalIndex: number;
}

interface ImportResult {
  success: boolean;
  companyName: string;
  corporateNumber?: string;
  companyAddress?: string;
  status: CustomerData['status'];
  error?: string;
  resultCount?: number; // 検索結果の件数（複数件ヒットした場合）
  originalIndex: number;
}

// 法人形態を検出して除去し、kindコードを取得（Functionsと同じロジック）
const detectAndRemoveCompanyType = (
  name: string
): { cleanedName: string; kind?: string } => {
  const companyTypeMap: { [key: string]: string } = {
    株式会社: '03',
    有限会社: '02',
    合名会社: '03',
    合資会社: '04',
    合同会社: '05',
  };

  let cleanedName = name.trim();
  let kind: string | undefined;

  for (const [type, code] of Object.entries(companyTypeMap)) {
    const escapedType = type.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedType, 'g');
    if (regex.test(cleanedName)) {
      cleanedName = cleanedName.replace(regex, '').trim();
      kind = code;
      break;
    }
  }

  return { cleanedName, kind };
};

const AdminImportPage: React.FC = () => {
  const { logout, adminUser } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<ImportResult[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 権限チェック：adminロールのみアクセス可能
  if (adminUser?.role !== 'admin') {
    return (
      <ProtectedRoute>
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              アクセス権限がありません
            </h1>
            <p className="text-gray-600 mb-4">
              このページは管理者のみアクセスできます。
            </p>
            <button
              onClick={() => (window.location.href = '/admin/companies')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              会社一覧に戻る
            </button>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  // CSV解析関数
  const parseCSV = (csvText: string): CSVRow[] => {
    const lines = csvText.split('\n');
    if (lines.length < 2) return [];

    // ヘッダー行を解析
    const headerLine = lines[0];
    const headers = headerLine.split(',').map(h => h.trim());

    // 会社名列と取引タイプ列のインデックスを取得（列名で検索）
    // ヘッダー行で列名を検索し、そのインデックスを使用してデータ行から値を取得
    const companyNameIndex = headers.findIndex(h => h === '会社名');
    const transactionTypeIndex = headers.findIndex(h => h === '取引タイプ');

    if (companyNameIndex === -1 || transactionTypeIndex === -1) {
      throw new Error('CSVに「会社名」または「取引タイプ」列が見つかりません');
    }

    // データ行を解析
    const rows: CSVRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // CSVの値を正確に分割（カンマで分割）
      let values = line.split(',').map(v => v.trim());
      // データ行の列数がヘッダーより多い場合の処理
      // データ行の最後に余分なカンマがある場合、最後の空文字列を削除
      while (
        values.length > headers.length &&
        values[values.length - 1] === ''
      ) {
        values.pop();
      }
      // データ行の列数がヘッダーより多い場合、最後の列の値を正しい位置に配置
      // ヘッダーの列数までに制限し、最後の列の値が正しい位置にあることを保証
      if (values.length > headers.length) {
        // 最後の列の値が正しい位置にない場合、最後の列の値を正しい位置に移動
        // values[headers.length - 1]が空文字列で、values[values.length - 1]に値がある場合
        if (values[headers.length - 1] === '' && values[values.length - 1]) {
          values[headers.length - 1] = values[values.length - 1];
        }
        // ヘッダーの列数までに制限
        values = values.slice(0, headers.length);
      }
      const trimmedValues = values;

      // ヘッダーで確認したインデックスを使用して値を取得
      // インデックスが範囲内の場合のみ値を取得
      const companyName =
        companyNameIndex >= 0 && companyNameIndex < trimmedValues.length
          ? trimmedValues[companyNameIndex] || ''
          : '';
      const transactionType =
        transactionTypeIndex >= 0 && transactionTypeIndex < trimmedValues.length
          ? trimmedValues[transactionTypeIndex] || ''
          : '';

      if (companyName) {
        rows.push({
          companyName,
          transactionType,
          originalIndex: i,
        });
      }
    }

    return rows;
  };

  // 取引タイプをステータスに変換
  const mapTransactionTypeToStatus = (
    transactionType: string
  ): CustomerData['status'] => {
    const trimmed = transactionType.trim();
    if (trimmed === '新規ビジネス') return 'new';
    if (trimmed === '既存ビジネス') return 'existing';
    if (trimmed === '復活ビジネス') return 'revived';
    return 'uncontracted'; // 空白またはその他
  };

  // 検索結果を処理してImportResultに変換
  // 完全一致するもののみを対象とする（複数件返ってきた場合でも完全一致があればそれを採用）
  const processSearchResult = (
    originalCompanyName: string,
    searchName: string,
    result: {
      success: boolean;
      data?: Array<{
        corporateNumber: string;
        name: string;
        address: string;
      }>;
      errorCode?: string;
      message?: string;
    }
  ):
    | { corporateNumber: string; address: string; resultCount: number }
    | { multipleResults: true; resultCount: number }
    | { errorCode: string; message: string }
    | null => {
    // エラーコード180（結果件数が多すぎ）の場合
    if (!result.success && result.errorCode === '180') {
      return {
        errorCode: '180',
        message:
          result.message ||
          '結果件数が多すぎます。検索条件を絞り込んでください。',
      };
    }

    if (result.success && result.data && result.data.length > 0) {
      // 元の会社名と完全一致するものを探す
      // 注意: APIから返ってくるnameは全角スペース・全角英字に変換されている可能性があるため、
      // 元の会社名も全角に変換してから比較する
      const convertHalfWidthToFullWidth = (text: string): string => {
        let result = '';
        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          const code = char.charCodeAt(0);
          // 半角英字 A-Z (0x41-0x5A) → 全角英字 Ａ-Ｚ (0xFF21-0xFF3A)
          if (code >= 0x41 && code <= 0x5a) {
            result += String.fromCharCode(0xff21 + (code - 0x41));
          } else if (code >= 0x61 && code <= 0x7a) {
            // 半角英字 a-z (0x61-0x7A) → 全角英字 ａ-ｚ (0xFF41-0xFF5A)
            result += String.fromCharCode(0xff41 + (code - 0x61));
          } else if (code >= 0x30 && code <= 0x39) {
            // 半角数字 0-9 (0x30-0x39) → 全角数字 ０-９ (0xFF10-0xFF19)
            result += String.fromCharCode(0xff10 + (code - 0x30));
          } else if (code === 0x20) {
            // 半角スペース (0x20) → 全角スペース (0x3000)
            result += String.fromCharCode(0x3000);
          } else {
            // その他の文字（日本語、全角英数字、全角スペースなど）はそのまま
            result += char;
          }
        }
        return result;
      };
      const normalizedOriginal =
        convertHalfWidthToFullWidth(originalCompanyName);
      const exactMatch = result.data.find(company => {
        // 完全一致をチェック（スペース・英字の違いを考慮）
        return (
          company.name === originalCompanyName ||
          company.name === normalizedOriginal
        );
      });

      if (exactMatch) {
        // 完全一致が見つかった場合
        return {
          corporateNumber: exactMatch.corporateNumber,
          address: exactMatch.address,
          resultCount: result.data.length,
        };
      }

      // 完全一致がない場合、複数件ヒットした場合は「絞れなかった」として扱う
      if (result.data.length > 1) {
        return {
          multipleResults: true,
          resultCount: result.data.length,
        };
      }

      // 1件のみヒットしたが完全一致しない場合も「絞れなかった」として扱う
      // （検索API側の仕様が不安定なため、部分一致のみでは登録しない）
      return {
        multipleResults: true,
        resultCount: 1,
      };
    }
    return null;
  };

  // CSVインポート処理
  const handleImport = async () => {
    if (!file) return;

    setIsProcessing(true);
    setResults([]);
    setProgress({ current: 0, total: 0 });

    try {
      const fileText = await file.text();
      const csvRows = parseCSV(fileText);

      // 全件処理（検証時は先頭10件のみの制限を削除）
      const rowsToProcess = csvRows; // 全件処理

      setProgress({ current: 0, total: rowsToProcess.length });

      const importResults: ImportResult[] = [];
      // 検索結果処理用の配列（ステップ1とステップ2で蓄積）
      const searchResultsToProcess: Array<{
        companyName: string;
        corporateNumber: string;
        address?: string;
        status: CustomerData['status'];
      }> = [];

      // ステップ1: 1000件ごとにDBキャッシュをチェック（Functions経由）
      const CACHE_BATCH_SIZE = 1000;
      const cacheMap = new Map<
        string,
        {
          searchName: string;
          kind?: string;
          cache: CompanySearchCache;
        }
      >();
      const processedRowIndices = new Set<number>(); // 処理済み行のインデックスを記録

      for (let i = 0; i < rowsToProcess.length; i += CACHE_BATCH_SIZE) {
        const batch = rowsToProcess.slice(i, i + CACHE_BATCH_SIZE);
        const searchQueries = batch.map(row => {
          const { cleanedName, kind } = detectAndRemoveCompanyType(
            row.companyName
          );
          return { searchName: cleanedName, kind };
        });

        // Functions経由でキャッシュを取得
        const batchGetCompanySearchCaches = httpsCallable<
          {
            searchQueries: Array<{ searchName: string; kind?: string }>;
            collectionPrefix?: string;
          },
          {
            success: boolean;
            data?: Array<{
              searchName: string;
              kind?: string;
              cache: {
                id: string;
                searchName: string;
                kind?: string;
                results: {
                  success: boolean;
                  data?: Array<{
                    corporateNumber: string;
                    name: string;
                    address: string;
                    corporateTypeCode?: string;
                  }>;
                  errorCode?: string;
                  message?: string;
                };
                lastSearchedAt: Date;
                createdAt: Date;
                updatedAt: Date;
              } | null;
            }>;
          }
        >(functions, 'batchGetCompanySearchCaches');

        const collectionPrefix =
          process.env.REACT_APP_FIREBASE_COLLECTION_PREFIX || '';
        const cacheResult = await batchGetCompanySearchCaches({
          searchQueries,
          collectionPrefix,
        });

        if (cacheResult.data.success && cacheResult.data.data) {
          // キャッシュ結果を処理
          // 注意: cacheResult.data.data.length と batch.length は一致しているはず
          if (cacheResult.data.data.length !== batch.length) {
            // eslint-disable-next-line no-console
            console.error(
              `[AdminImportPage] キャッシュ結果の数が一致しません: ` +
                `batch.length=${batch.length}, ` +
                `cacheResult.data.data.length=${cacheResult.data.data.length}`
            );
          }

          for (let j = 0; j < cacheResult.data.data.length; j++) {
            const cacheItem = cacheResult.data.data[j];
            const row = batch[j];
            const rowIndex = i + j; // 元のインデックス
            const cacheKey = cacheItem.kind
              ? `${cacheItem.searchName}_kind_${cacheItem.kind}`
              : cacheItem.searchName;

            if (cacheItem.cache) {
              // キャッシュが見つかった場合
              cacheMap.set(cacheKey, {
                searchName: cacheItem.searchName,
                kind: cacheItem.kind,
                cache: cacheItem.cache,
              });

              // 処理済みとして記録
              processedRowIndices.add(rowIndex);
              // eslint-disable-next-line no-console
              console.log(
                `[AdminImportPage] キャッシュが見つかりました: ` +
                  `rowIndex=${rowIndex}, ` +
                  `companyName=${row.companyName}, ` +
                  `cacheKey=${cacheKey}`
              );

              // キャッシュから結果を処理して即座に進捗バーを更新
              const searchResult = processSearchResult(
                row.companyName, // 元の会社名を渡す
                cacheItem.searchName,
                cacheItem.cache.results
              );

              if (!searchResult) {
                importResults.push({
                  success: false,
                  companyName: row.companyName,
                  status: mapTransactionTypeToStatus(row.transactionType),
                  error: '法人番号検索にヒットしませんでした',
                  originalIndex: row.originalIndex,
                });
              } else if ('errorCode' in searchResult) {
                // エラーコード180（結果件数が多すぎ）の場合
                importResults.push({
                  success: false,
                  companyName: row.companyName,
                  status: mapTransactionTypeToStatus(row.transactionType),
                  error: searchResult.message,
                  originalIndex: row.originalIndex,
                });
              } else if (
                'multipleResults' in searchResult &&
                searchResult.multipleResults
              ) {
                importResults.push({
                  success: false,
                  companyName: row.companyName,
                  status: mapTransactionTypeToStatus(row.transactionType),
                  error: `検索結果が${searchResult.resultCount}件あり、完全一致するものが見つかりませんでした`,
                  resultCount: searchResult.resultCount,
                  originalIndex: row.originalIndex,
                });
              } else if ('corporateNumber' in searchResult) {
                // 完全一致が見つかった場合、検索結果を配列に追加（後でFunctions APIで一括処理）
                const status = mapTransactionTypeToStatus(row.transactionType);
                searchResultsToProcess.push({
                  companyName: row.companyName,
                  corporateNumber: searchResult.corporateNumber,
                  address: searchResult.address,
                  status,
                });

                importResults.push({
                  success: true,
                  companyName: row.companyName,
                  corporateNumber: searchResult.corporateNumber,
                  companyAddress: searchResult.address,
                  status,
                  originalIndex: row.originalIndex,
                });
              }

              // 進捗バーを更新（キャッシュから結果が得られた分）
              setProgress({
                current: importResults.length,
                total: rowsToProcess.length,
              });
            }
          }
        }
      }

      // ステップ1終了時：検索結果をFunctions APIで一括処理（1000件ずつ）
      let step1ProcessedCount = 0;
      if (searchResultsToProcess.length > 0) {
        const PROCESS_BATCH_SIZE = 1000;
        for (
          let i = 0;
          i < searchResultsToProcess.length;
          i += PROCESS_BATCH_SIZE
        ) {
          const batch = searchResultsToProcess.slice(i, i + PROCESS_BATCH_SIZE);
          try {
            const batchProcessSearchResults = httpsCallable<
              {
                results: Array<{
                  companyName: string;
                  corporateNumber: string;
                  address?: string;
                  status: CustomerData['status'];
                }>;
                collectionPrefix?: string;
              },
              {
                success: boolean;
                data?: Array<{
                  corporateNumber: string;
                  success: boolean;
                  isNew: boolean;
                  error?: string;
                }>;
              }
            >(functions, 'batchProcessSearchResults');

            const collectionPrefix =
              process.env.REACT_APP_FIREBASE_COLLECTION_PREFIX || '';
            const result = await batchProcessSearchResults({
              results: batch,
              collectionPrefix,
            });

            if (result.data.success && result.data.data) {
              // エラーがあった場合はログに記録
              const errors = result.data.data.filter(r => !r.success);
              if (errors.length > 0) {
                // eslint-disable-next-line no-console
                console.error(
                  `[AdminImportPage] 検索結果処理エラー: ${errors.length}件`,
                  errors
                );
              }
            }
            step1ProcessedCount += batch.length;
          } catch (error: any) {
            // eslint-disable-next-line no-console
            console.error('検索結果処理エラー:', error);
          }
        }
      }

      // ステップ2: キャッシュがないもののみFunctionsを呼び出す（10件ごと）
      const BATCH_SIZE = 10;
      const companiesToSearch: Array<{
        row: CSVRow;
        searchName: string;
        kind?: string;
      }> = [];

      for (let i = 0; i < rowsToProcess.length; i++) {
        // 処理済みの行はスキップ
        if (processedRowIndices.has(i)) {
          // eslint-disable-next-line no-console
          console.log(
            `[AdminImportPage] 処理済み行をスキップ: ` +
              `rowIndex=${i}, ` +
              `companyName=${rowsToProcess[i].companyName}`
          );
          continue;
        }

        const row = rowsToProcess[i];
        const { cleanedName, kind } = detectAndRemoveCompanyType(
          row.companyName
        );
        const cacheKey = kind ? `${cleanedName}_kind_${kind}` : cleanedName;
        const cached = cacheMap.get(cacheKey);

        if (!cached) {
          companiesToSearch.push({ row, searchName: cleanedName, kind });
          // eslint-disable-next-line no-console
          console.log(
            `[AdminImportPage] 検索対象に追加: ` +
              `rowIndex=${i}, ` +
              `companyName=${row.companyName}, ` +
              `cacheKey=${cacheKey}`
          );
        } else {
          // eslint-disable-next-line no-console
          console.log(
            `[AdminImportPage] キャッシュが見つかりました（ステップ2）: ` +
              `rowIndex=${i}, ` +
              `companyName=${row.companyName}, ` +
              `cacheKey=${cacheKey}`
          );
        }
      }

      // 10件ごとにFunctionsを呼び出し
      // eslint-disable-next-line no-console
      console.log(
        `[AdminImportPage] ステップ2開始: ` +
          `companiesToSearch.length=${companiesToSearch.length}, ` +
          `processedRowIndices.size=${processedRowIndices.size}, ` +
          `rowsToProcess.length=${rowsToProcess.length}`
      );

      for (let i = 0; i < companiesToSearch.length; i += BATCH_SIZE) {
        const batch = companiesToSearch.slice(i, i + BATCH_SIZE);
        const companyNames = batch.map(b => b.row.companyName);

        // eslint-disable-next-line no-console
        console.log(
          `[AdminImportPage] batchSearchCompanies呼び出し: ` +
            `batchIndex=${i}, ` +
            `batchSize=${batch.length}, ` +
            `companyNames=${companyNames.join(', ')}`
        );

        try {
          const batchSearchCompanies = httpsCallable<
            {
              companies: string[];
              collectionPrefix?: string;
            },
            {
              success: boolean;
              data?: Array<{
                companyName: string;
                searchName: string;
                kind?: string;
                result: {
                  success: boolean;
                  data?: Array<{
                    corporateNumber: string;
                    name: string;
                    address: string;
                    corporateTypeCode?: string;
                  }>;
                  errorCode?: string;
                  message?: string;
                };
              }>;
            }
          >(functions, 'batchSearchCompanies');

          const collectionPrefix =
            process.env.REACT_APP_FIREBASE_COLLECTION_PREFIX || '';
          const result = await batchSearchCompanies({
            companies: companyNames,
            collectionPrefix,
          });

          if (result.data.success && result.data.data) {
            // バッチ結果を処理
            for (const batchResult of result.data.data) {
              const batchItem = batch.find(
                b => b.row.companyName === batchResult.companyName
              );
              if (!batchItem) continue;

              const searchResult = processSearchResult(
                batchItem.row.companyName, // 元の会社名を渡す
                batchResult.searchName,
                batchResult.result
              );

              // インポート結果に追加（後で処理）
              const row = batchItem.row;
              if (!searchResult) {
                importResults.push({
                  success: false,
                  companyName: row.companyName,
                  status: mapTransactionTypeToStatus(row.transactionType),
                  error: '法人番号検索にヒットしませんでした',
                  originalIndex: row.originalIndex,
                });
              } else if ('errorCode' in searchResult) {
                // エラーコード180（結果件数が多すぎ）の場合
                importResults.push({
                  success: false,
                  companyName: row.companyName,
                  status: mapTransactionTypeToStatus(row.transactionType),
                  error: searchResult.message,
                  originalIndex: row.originalIndex,
                });
              } else if (
                'multipleResults' in searchResult &&
                searchResult.multipleResults
              ) {
                importResults.push({
                  success: false,
                  companyName: row.companyName,
                  status: mapTransactionTypeToStatus(row.transactionType),
                  error: `検索結果が${searchResult.resultCount}件あり、完全一致するものが見つかりませんでした`,
                  resultCount: searchResult.resultCount,
                  originalIndex: row.originalIndex,
                });
              } else if ('corporateNumber' in searchResult) {
                // 完全一致が見つかった場合、検索結果を配列に追加（後でFunctions APIで一括処理）
                const status = mapTransactionTypeToStatus(row.transactionType);
                searchResultsToProcess.push({
                  companyName: row.companyName,
                  corporateNumber: searchResult.corporateNumber,
                  address: searchResult.address,
                  status,
                });

                importResults.push({
                  success: true,
                  companyName: row.companyName,
                  corporateNumber: searchResult.corporateNumber,
                  companyAddress: searchResult.address,
                  status,
                  originalIndex: row.originalIndex,
                });
              }

              // 進捗バーを更新（実際にAPI結果が取得できて処理が完了した分のみ）
              setProgress({
                current: importResults.length,
                total: rowsToProcess.length,
              });
            }
          }
        } catch (error: any) {
          // eslint-disable-next-line no-console
          console.error('Batch search error:', error);
          // エラーが発生した場合、該当バッチの全件をエラーとして記録
          for (const batchItem of batch) {
            importResults.push({
              success: false,
              companyName: batchItem.row.companyName,
              status: mapTransactionTypeToStatus(batchItem.row.transactionType),
              error: error.message || '検索処理中にエラーが発生しました',
              originalIndex: batchItem.row.originalIndex,
            });
          }

          // 進捗バーを更新（エラーで処理が完了した分）
          setProgress({
            current: importResults.length,
            total: rowsToProcess.length,
          });
        }
      }

      // ステップ2終了時：検索結果をFunctions APIで一括処理（1000件ずつ）
      // ステップ1で処理済みの分をスキップし、ステップ2で追加した分のみを処理
      if (searchResultsToProcess.length > step1ProcessedCount) {
        const PROCESS_BATCH_SIZE = 1000;
        const step2SearchResults =
          searchResultsToProcess.slice(step1ProcessedCount);

        for (
          let i = 0;
          i < step2SearchResults.length;
          i += PROCESS_BATCH_SIZE
        ) {
          const batch = step2SearchResults.slice(i, i + PROCESS_BATCH_SIZE);
          try {
            const batchProcessSearchResults = httpsCallable<
              {
                results: Array<{
                  companyName: string;
                  corporateNumber: string;
                  address?: string;
                  status: CustomerData['status'];
                }>;
                collectionPrefix?: string;
              },
              {
                success: boolean;
                data?: Array<{
                  corporateNumber: string;
                  success: boolean;
                  isNew: boolean;
                  error?: string;
                }>;
              }
            >(functions, 'batchProcessSearchResults');

            const collectionPrefix =
              process.env.REACT_APP_FIREBASE_COLLECTION_PREFIX || '';
            const result = await batchProcessSearchResults({
              results: batch,
              collectionPrefix,
            });

            if (result.data.success && result.data.data) {
              // エラーがあった場合はログに記録
              const errors = result.data.data.filter(r => !r.success);
              if (errors.length > 0) {
                // eslint-disable-next-line no-console
                console.error(
                  `[AdminImportPage] 検索結果処理エラー: ${errors.length}件`,
                  errors
                );
              }
            }
          } catch (error: any) {
            // eslint-disable-next-line no-console
            console.error('検索結果処理エラー:', error);
          }
        }
      }

      // 結果をソート：絞れなかった → 対象なし → 成功の順
      // 注意: resultCount > 0 で完全一致が見つからなかったものを抽出
      // （1件でも完全一致がない場合は「絞れなかった」として扱う）
      const multipleResults = importResults.filter(
        r => !r.success && r.resultCount && r.resultCount > 0
      );
      const noResults = importResults.filter(r => !r.success && !r.resultCount);
      const successResults = importResults.filter(r => r.success);
      const sortedResults = [
        ...multipleResults,
        ...noResults,
        ...successResults,
      ];

      setResults(sortedResults);
      setProgress({
        current: rowsToProcess.length,
        total: rowsToProcess.length,
      });
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Import error:', error);
      alert(`インポートエラー: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResults([]);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/admin/login';
  };

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h1 className="text-2xl font-bold text-gray-900">
                  CSVインポート
                </h1>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <span>ログアウト</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CSVファイルを選択
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                disabled={isProcessing}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="mt-2 text-sm text-gray-500">
                「会社名」列と「取引タイプ」列を含むCSVファイルを選択してください。
              </p>
            </div>

            {isProcessing && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    処理中...
                  </span>
                  <span className="text-sm text-gray-500">
                    {progress.current} / {progress.total}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${(progress.current / progress.total) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            )}

            <div className="mb-6">
              <button
                onClick={handleImport}
                disabled={!file || isProcessing}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isProcessing ? '処理中...' : 'インポート開始'}
              </button>
            </div>

            {results.length > 0 && (
              <div className="mt-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  インポート結果 ({results.length}件)
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          結果
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          会社名
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          法人番号
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ステータス
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          エラー/件数
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {results.map((result, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {result.success ? (
                              <span className="text-green-600 font-medium">
                                ✓ 成功
                              </span>
                            ) : (
                              <span className="text-red-600 font-medium">
                                ✗ 失敗
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {result.companyName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {result.corporateNumber || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {result.status === 'uncontracted' && '未契約'}
                            {result.status === 'new' && '新規'}
                            {result.status === 'existing' && '既存'}
                            {result.status === 'revived' && '復活'}
                          </td>
                          <td className="px-6 py-4 text-sm text-red-600">
                            {result.resultCount && result.resultCount > 1 ? (
                              <span>
                                {result.error} ({result.resultCount}件)
                              </span>
                            ) : (
                              <span>{result.error || '-'}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </ProtectedRoute>
  );
};

export default AdminImportPage;
